import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Button,
  Divider,
  Tooltip,
  Collapse
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { getGameSystems, handlePagingAll } from "../../components/api/APIUtilities";
import { getData } from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import GenericPicker from "../../components/utilities/GenericPicker";
import OverallStats from "./components/OverallStats";
import FactionRankings from "./components/FactionRankings";
import ScenariosStats from "./components/ScenariosStats";
import FactionAnalyzer from "./components/FactionAnalyzer";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import DatePickerComponent from "../../components/utilities/DatePickerComponent";
import GameSystemPicker from "../../components/utilities/gamesystempickers/GameSystemPicker";
import {
  BCPCircleInfo,
  BCPAngleDown,
  BCPAngleUp
} from "../../components/icons/bcp/BCPIcons";

const ROUNDS_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
  label: `${n} Rounds`,
  value: n
}));


export default function Stats() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(true);
  const [gameSystems, setGameSystems] = useState([]);
  const [selectedGameSystem, setSelectedGameSystem] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [factionData, setFactionData] = useState(null);
  const [scenariosData, setScenariosData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [factionAnalysisData, setFactionAnalysisData] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    numberOfRounds: ""
  });
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const factionRankingsRef = useRef(null);

  const hasActiveFilters = Boolean(
    filters.startDate || filters.endDate || filters.numberOfRounds
  );

  const handleClearFilters = () => {
    setFilters({ startDate: null, endDate: null, numberOfRounds: "" });
    setSelectedMilestone(null);
  };

  const handleMilestoneChange = (e) => {
    const milestone = milestones.find((m) => m.id === e.target.value) || null;
    setSelectedMilestone(milestone);
    if (milestone) {
      setFilters((prev) => ({
        ...prev,
        startDate: milestone.milestoneDate,
        endDate: milestone.milestoneEndDate || null
      }));
    }
  };

  const handleDateChange = (name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val }));
    setSelectedMilestone(null);
  };

  const handleBack = () => {
    setFactionAnalysisData(null);
    setTimeout(() => {
      factionRankingsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 50);
  };

  useEffect(() => {
    const loadGameSystems = async () => {
      try {
        setLoading(true);
        let filters = {};
        if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
          filters.gameSystemId = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
        } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
          filters.gameType = getEnvironmentVariable("SINGLE_GAME_TYPE");
        } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
          filters.manufacturer = getEnvironmentVariable(
            "SINGLE_GAME_MANUFACTURER"
          );
        }

        const systems = await getGameSystems(filters);
        // Keep as !== false for now, when swapping to API, use supportsStats === true
        const systemsWithStats =
          systems?.filter((gs) => gs.supportsStats !== false) || [];
        setGameSystems(systemsWithStats);

        if (systemsWithStats?.length === 1) {
          setSelectedGameSystem(systemsWithStats[0]);
        }

        setLoading(false);
      } catch (err) {
        postNotification("error", "Failed to load game systems");
        setLoading(false);
      }
    };

    loadGameSystems();
  }, []);

  useEffect(() => {
    if (!selectedGameSystem?.id) {
      setMilestones([]);
      setSelectedMilestone(null);
      return;
    }
    setFilters({ startDate: null, endDate: null, numberOfRounds: "" });
    setSelectedMilestone(null);
    const loadMilestones = async () => {
      const data = await handlePagingAll("/milestones", {
        gameSystemId: selectedGameSystem.id
      });
      setMilestones((data || []).filter((m) => m.active));
    };
    loadMilestones();
  }, [selectedGameSystem]);

  useEffect(() => {
    if (!selectedGameSystem?.id) {
      setStatsData(null);
      setFactionData(null);
      setScenariosData(null);
      setFactionAnalysisData(null);
      return;
    }

    const loadStats = async () => {
      if (
        filters.startDate &&
        filters.endDate &&
        moment(filters.endDate).isBefore(moment(filters.startDate))
      ) {
        postNotification("error", "End date cannot be before start date");
        return;
      }

      try {
        setLoadingStats(true);

        const queryParams = { leagueId: "78WQ5wCPefGH" };
        if (filters.startDate) {
          queryParams.startDate = moment(filters.startDate).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.endDate) {
          queryParams.endDate = moment(filters.endDate).format("YYYY-MM-DD");
        }
        if (filters.numberOfRounds) {
          queryParams.numberOfRounds = filters.numberOfRounds;
        }

        const apiData = await getData("/stats", {
          queryStringParameters: queryParams
        });

        setStatsData({
          gameSystem: apiData.gameSystem,
          charts: [
            {
              label: "Total",
              rows: [
                {
                  header: true,
                  cols: [null, "Events", "Games", "Games per Event"]
                },
                {
                  cols: [
                    "Games",
                    apiData.overview.events,
                    apiData.overview.games,
                    apiData.overview.avgGames
                  ]
                }
              ]
            },
            {
              label: "Format",
              rows: [
                {
                  header: true,
                  cols: [null, "Events", "Games", "Games per Event"]
                },
                {
                  cols: [
                    "Singles",
                    apiData.format.singles.events,
                    apiData.format.singles.games,
                    apiData.format.singles.avgGames
                  ]
                },
                {
                  cols: [
                    "Doubles",
                    apiData.format.doubles.events,
                    apiData.format.doubles.games,
                    apiData.format.doubles.avgGames
                  ]
                },
                {
                  cols: [
                    "Teams",
                    apiData.format.teams.events,
                    apiData.format.teams.games,
                    apiData.format.teams.avgGames
                  ]
                }
              ]
            },
            {
              label: "Style",
              rows: [
                {
                  header: true,
                  cols: [null, "Events", "Games", "Games per Event"]
                },
                {
                  cols: [
                    "Tournament",
                    apiData.eventStyle.tournament.events,
                    apiData.eventStyle.tournament.games,
                    apiData.eventStyle.tournament.avgGames
                  ]
                },
                {
                  cols: [
                    "League",
                    apiData.eventStyle.league.events,
                    apiData.eventStyle.league.games,
                    apiData.eventStyle.league.avgGames
                  ]
                }
              ]
            }
          ]
        });

        setFactionData({
          gameSystem: apiData.gameSystem,
          label: apiData.leaderboard.label,
          childLabel: apiData.leaderboard.childLabel,
          childRecordType: "army",
          rows: apiData.leaderboard.rows.map((row, index) => ({
            ...row,
            position: index + 1
          }))
        });

        setScenariosData({
          gameSystem: apiData.gameSystem,
          missions: [],
          winConditions: [],
          firstSecond: [
            {
              label: "First Player",
              first: true,
              games: apiData.priorityTurn.first.games,
              winRate: apiData.priorityTurn.first.winRate
            },
            {
              label: "Second Player",
              second: true,
              games: apiData.priorityTurn.second.games,
              winRate: apiData.priorityTurn.second.winRate
            }
          ],
          pointSize: []
        });

        setLoadingStats(false);
      } catch (err) {
        postNotification("error", "Failed to load statistics");
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [
    selectedGameSystem,
    filters.startDate,
    filters.endDate,
    filters.numberOfRounds
  ]);

  const generateMockFactionAnalysisData = (row, allRows = []) => {
    const totalGames = row.stats.games;
    const attackerGames = Math.floor(totalGames * 0.52);
    const defenderGames = totalGames - attackerGames;

    const clamp = (v) => Math.min(65, Math.max(35, v));
    const attackerWinRate = clamp(
      Math.round(row.stats.winRate + (Math.random() - 0.5) * 12)
    );
    const defenderWinRate = clamp(
      Math.round(row.stats.winRate + (Math.random() - 0.5) * 12)
    );

    const now = new Date();

    const weeklyTimeline = [];
    for (let i = 15; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const label = d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });
      weeklyTimeline.push({
        id: label,
        games: Math.floor(Math.random() * 70) + 10,
        winRate: clamp(
          Math.round(row.stats.winRate + (Math.random() - 0.5) * 20)
        )
      });
    }

    const monthlyTimeline = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const label = d.toLocaleDateString("en-US", { month: "short" });
      monthlyTimeline.push({
        id: label,
        games: Math.floor(Math.random() * 280) + 40,
        winRate: clamp(
          Math.round(row.stats.winRate + (Math.random() - 0.5) * 15)
        )
      });
    }

    const clampEnemy = (v) => Math.min(80, Math.max(20, v));
    const enemyFactions = allRows
      .filter((r) => r.faction.id !== row.faction.id)
      .map((enemyRow) => {
        const baseWinRate = clampEnemy(
          Math.round(50 + (Math.random() - 0.5) * 50)
        );
        const atkGames = Math.floor(Math.random() * 80) + 5;
        const defGames = Math.floor(Math.random() * 80) + 5;
        const atkWinRate = clampEnemy(
          Math.round(baseWinRate + (Math.random() - 0.5) * 20)
        );
        const defWinRate = clampEnemy(
          Math.round(baseWinRate + (Math.random() - 0.5) * 20)
        );
        const overallWinRate = Math.round(
          (atkWinRate * atkGames + defWinRate * defGames) /
            (atkGames + defGames)
        );
        return {
          faction: enemyRow.faction,
          overall: { games: atkGames + defGames, winRate: overallWinRate },
          asAttacker: { games: atkGames, winRate: atkWinRate },
          asDefender: { games: defGames, winRate: defWinRate },
          vsLeaders: (enemyRow.subFactions || []).map((sf) => ({
            name: sf.faction.name,
            icon: sf.faction.icon || null,
            games: Math.floor(Math.random() * 80) + 5,
            winRate: clampEnemy(
              Math.round(baseWinRate + (Math.random() - 0.5) * 25)
            )
          }))
        };
      });

    const RATING_GROUPS = [
      "0-100",
      "100-200",
      "200-300",
      "300-400",
      "400-500",
      "500-600",
      "600-700"
    ];
    const ratingWeights = [0.08, 0.12, 0.1, 0.12, 0.15, 0.18, 0.25];
    let ratingRemainingGames = totalGames;
    const playerRatings = RATING_GROUPS.map((group, i) => {
      const isLast = i === RATING_GROUPS.length - 1;
      const groupGames = isLast
        ? ratingRemainingGames
        : Math.round(totalGames * ratingWeights[i]);
      ratingRemainingGames -= groupGames;
      const players = Math.max(
        1,
        Math.round(groupGames * (0.04 + Math.random() * 0.06))
      );
      const gamesPerPlayer = parseFloat((groupGames / players).toFixed(1));
      const winRate = clamp(
        Math.round(row.stats.winRate + (Math.random() - 0.5) * 20)
      );
      return { id: group, games: groupGames, winRate, players, gamesPerPlayer };
    });

    const clampScenario = (v) => Math.min(80, Math.max(20, v));

    const scenarios = scenariosData
      ? {
          gameSystem: scenariosData.gameSystem,
          missions: (scenariosData.missions || []).map((m) => ({
            mission: m.mission,
            games: Math.floor(m.games * (0.05 + Math.random() * 0.15))
          })),
          winConditions: (scenariosData.winConditions || []).map((w) => ({
            winCondition: w.winCondition,
            games: Math.floor(w.games * (0.05 + Math.random() * 0.15))
          })),
          firstSecond: [
            {
              label: "First Player",
              first: true,
              games: Math.floor(totalGames * 0.52),
              winRate: clampScenario(
                Math.round(row.stats.winRate + (Math.random() - 0.5) * 10)
              )
            },
            {
              label: "Second Player",
              second: true,
              games: Math.floor(totalGames * 0.48),
              winRate: clampScenario(
                Math.round(100 - row.stats.winRate + (Math.random() - 0.5) * 10)
              )
            }
          ],
          pointSize: (scenariosData.pointSize || []).map((p) => ({
            label: p.label,
            games: Math.floor(p.games * (0.05 + Math.random() * 0.15))
          }))
        }
      : null;

    return {
      faction: row.faction,
      overall: { games: totalGames, winRate: row.stats.winRate },
      asAttacker: { games: attackerGames, winRate: attackerWinRate },
      asDefender: { games: defenderGames, winRate: defenderWinRate },
      leaders: (row.subFactions || []).map((sf) => ({
        name: sf.faction.name,
        icon: sf.faction.icon || null,
        games: sf.stats.games,
        winRate: sf.stats.winRate
      })),
      weeklyTimeline,
      monthlyTimeline,
      enemyFactions,
      scenarios,
      playerRatings
    };
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          pt: 2,
          pb: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px"
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!gameSystems || gameSystems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No game systems with statistics available
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: 2,
        pb: 4
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textAlign: "center",
          letterSpacing: "0.1em",
          textTransform: "uppercase"
        }}
      >
        Game System Statistics
      </Typography>

      <Box sx={{ mb: 3 }}>
        <GameSystemPicker
          name="gameSystemId"
          value={selectedGameSystem?.id || ""}
          valueType="id"
          handleChange={(e) => {
            if (e.target.value) {
              const selected = gameSystems.find(
                (gs) => gs.id === e.target.value
              );
              setSelectedGameSystem(selected);
            } else {
              setSelectedGameSystem(null);
            }
          }}
          gameSystems={gameSystems}
          required
        />
      </Box>

      {selectedGameSystem && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            borderRadius: 1,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.02)"
          }}
        >
          <Box
            onClick={() => setFiltersOpen((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: filtersOpen ? 2 : 0,
              cursor: "pointer",
              userSelect: "none"
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em"
              }}
            >
              Filters
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {filtersOpen && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.disabled,
                    fontStyle: "italic"
                  }}
                >
                  All fields optional
                </Typography>
              )}
              {filtersOpen ? (
                <BCPAngleUp size="sm" />
              ) : (
                <BCPAngleDown size="sm" />
              )}
            </Box>
          </Box>
          <Collapse in={filtersOpen}>
            <Divider sx={{ mb: 2.5 }} />
            <Grid container spacing={2} alignItems="flex-start">
              {milestones.filter((m) => m.active).length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <GenericPicker
                        name="milestone"
                        label="Milestone"
                        value={selectedMilestone?.id || ""}
                        handleChange={handleMilestoneChange}
                        list={milestones
                          .filter((m) => m.active)
                          .map((m) => ({ label: m.name, value: m.id }))}
                        inclAny={false}
                        clearable
                        clearLabel="None"
                      />
                    </Box>
                    <Tooltip
                      title="Milestones mark key game updates or edition launches. Selecting one automatically filters the stats to that time period, so you can see how the meta looked at that point in the game's history."
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          display: "flex",
                          cursor: "default",
                          color: "text.secondary"
                        }}
                      >
                        <BCPCircleInfo size="sm" />
                      </Box>
                    </Tooltip>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <DatePickerComponent
                  name="startDate"
                  label="Start Date"
                  value={filters.startDate}
                  setFieldValue={handleDateChange}
                  statsNullable
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePickerComponent
                  name="endDate"
                  label="End Date"
                  value={filters.endDate}
                  setFieldValue={handleDateChange}
                  statsNullable
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GenericPicker
                  name="numberOfRounds"
                  label="Number of Rounds"
                  value={filters.numberOfRounds}
                  handleChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      numberOfRounds: e.target.value
                    }))
                  }
                  list={ROUNDS_OPTIONS}
                  inclAny={false}
                  clearable
                  clearLabel="Any"
                />
              </Grid>
              {hasActiveFilters && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </Box>
      )}

      {!selectedGameSystem && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Please select a game system to view statistics
          </Typography>
        </Box>
      )}

      {loadingStats && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingStats && statsData && selectedGameSystem && (
        <>
          {factionAnalysisData ? (
            <FactionAnalyzer
              data={factionAnalysisData}
              theme={theme}
              onBack={handleBack}
              factionRows={factionData?.rows}
              onFactionChange={(row) =>
                setFactionAnalysisData(
                  generateMockFactionAnalysisData(row, factionData?.rows)
                )
              }
            />
          ) : (
            <>
              <OverallStats data={statsData} theme={theme} />
              <Box ref={factionRankingsRef}>
                <FactionRankings
                  data={factionData}
                  label={factionData?.label}
                  childLabel={factionData?.childLabel}
                  childRecordType={factionData?.childRecordType}
                  theme={theme}
                  onAnalyze={(row) =>
                    setFactionAnalysisData(
                      generateMockFactionAnalysisData(row, factionData?.rows)
                    )
                  }
                />
              </Box>
              <ScenariosStats data={scenariosData} theme={theme} />
            </>
          )}
        </>
      )}
    </Container>
  );
}
